import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { notificationService } from '../lib/audio/notificationService';
import { analyticsService } from '../lib/analytics/analyticsService';

interface LivechatFrameProps {
  url: string;
  onClose: () => void;
  onMessage?: () => void;
  config: any;
  visible: boolean;
}

export const LivechatFrame: React.FC<LivechatFrameProps> = ({
  url,
  onClose,
  config,
  visible,
  onMessage
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && iframeRef.current) {
      iframeRef.current.src = url;
      initializedRef.current = true;

      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'livechat_open',
        event_data: {
          url
        }
      });
    }
  }, [url]);

  useEffect(() => {
    if (visible) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin === 'https://app.ideta.io') {
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'livechat_message_received',
          event_data: {
            visible: false,
            app_state: document.visibilityState
          }
        });

        if (document.visibilityState === 'hidden' || !visible) {
          onMessage?.();
          await notificationService.play();

          if (window.matchMedia('(display-mode: standalone)').matches && 'Notification' in window) {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                new Notification('Nouveau message', {
                  icon: '/icon/icon_192.png',
                  badge: '/icon/icon_192.png',
                  tag: 'livechat-message'
                });
              }
            });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [visible, onMessage]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'livechat_close',
        event_data: {
          close_method: 'overlay'
        }
      });

      onClose();
    }
  };

  const handleCloseClick = () => {
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'livechat_close',
      event_data: {
        close_method: 'button'
      }
    });

    onClose();
  };

  return (
    <div 
      className="fixed inset-0 transition-all duration-200 livechat-modal"
      style={{
        backgroundColor: visible ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
        zIndex: 9999,
        pointerEvents: visible ? 'auto' : 'none',
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden'
      }}
      onClick={handleOverlayClick}
    >
      <div 
        className="absolute inset-4 flex flex-col overflow-hidden transition-transform duration-200 mx-auto"
        style={{ 
          borderRadius: config.border_radius,
          transform: visible ? 'scale(1)' : 'scale(0.95)',
          opacity: visible ? 1 : 0,
          backgroundColor: '#FFFFFF',
          maxWidth: '700px'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div 
          className="flex justify-end p-2 border-b"
          style={{ backgroundColor: config['dark-color'] }}
        >
          <button
            onClick={handleCloseClick}
            className="p-2 rounded-full text-white hover:bg-white/10 transition-colors"
            aria-label="Fermer le chat"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 w-full bg-white">
          <iframe
            ref={iframeRef}
            className="w-full h-full border-0"
            title="Livechat"
            allow="microphone; camera"
          />
        </div>
      </div>
    </div>
  );
};